import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Send, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useCampaignProgressTracker } from '@/contexts/CampaignProgressContext';
import { useCampaignProgress } from '@/hooks/useCampaignProgress';

export default function CampaignProgressToast() {
  const { tracked, stopTracking } = useCampaignProgressTracker();
  const [minimized, setMinimized] = useState(false);
  const queryClient = useQueryClient();
  const { data } = useCampaignProgress(tracked?.campaignId ?? null);

  const isFinished = !!data?.isFinished;

  // Rafraîchir la liste des campagnes quand l'envoi est terminé
  useEffect(() => {
    if (isFinished) {
      queryClient.invalidateQueries({ queryKey: ['email-campaigns'] });
    }
  }, [isFinished, queryClient]);

  useEffect(() => {
    if (tracked) setMinimized(false);
  }, [tracked?.campaignId]);

  if (!tracked) return null;

  const total = data?.total || tracked.expectedTotal || 0;
  const processed = (data?.sent ?? 0) + (data?.failed ?? 0);
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const failed = data?.failed ?? 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))]">
      <div className="rounded-xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          {isFinished ? (
            failed > 0 ? (
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
            )
          ) : (
            <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />
          )}
          <p className="text-sm font-semibold truncate flex-1">
            {isFinished
              ? failed > 0
                ? `Terminée avec ${failed} échec(s)`
                : 'Campagne envoyée'
              : 'Envoi en cours...'}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setMinimized((m) => !m)}
            aria-label={minimized ? 'Agrandir' : 'Réduire'}
          >
            {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={stopTracking}
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {!minimized && (
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Send className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground line-clamp-2">{tracked.name}</p>
            </div>

            <Progress value={percent} className="h-2" />

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {processed} / {total || '—'} emails traités
              </span>
              <span className={cn('font-semibold', isFinished && failed === 0 && 'text-primary')}>
                {percent}%
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <span className="text-primary font-medium">{data?.sent ?? 0} envoyé(s)</span>
              {failed > 0 && (
                <span className="text-destructive font-medium">{failed} échec(s)</span>
              )}
            </div>

            {!isFinished && (
              <p className="text-[11px] text-muted-foreground">
                Vous pouvez continuer à naviguer, l'envoi se poursuit en arrière-plan.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
